

export const exists = (value) => {
    return !([undefined, null].includes(value) || typeof value === 'undefined')
};

export const isValidCode = (value) => {
    if (typeof value !== 'string') return false;
    const regex = /^[0-9]{6}$/;
    return regex.test(value);
}

export const isValidEmail = (value) => {
    if (typeof value !== 'string') return false;
    const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(value);
};

export const isValidUrl = (value) => {
    if (typeof value !== 'string') return false;
    const regex = /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i;
    return regex.test(value);
};

export const isValidPassword = (value) => {
    if (typeof value !== 'string') return false;
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{10,}$/;
    return regex.test(value);
};

export const isValidPasswordV2 = (value) => {
    if (typeof value !== 'string') return false;
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\s]).{8,}$/;
    return regex.test(value);
}

export const isNotEmpty = (value) => {
    if (!exists(value)) return false;
    if (Array.isArray(value)) return value.length !== 0;

    switch (typeof value) {
        case 'bigint': case 'number': return value !== 0;
        case 'boolean': return value === true;
        case 'object': return Object.keys(value)?.length > 0;
        case 'string': return value.trim() !== '';
        case 'symbol': return value.toString().trim() !== '';
        case 'function': return typeof value() === 'undefined';
        default: return false;
    }
}

export const isEmpty = (value) => !isNotEmpty(value);

export const isValidNumber = (value, checkOnlyString = false) => {
    if (typeof value === 'number' && !checkOnlyString) return true;
    else if (typeof value === 'string') return (!isNaN(value) && !isNaN(parseFloat(value)));
    return false;
};

export const isValidObjectId = (value) => {
    const regex = /^[a-f\d]{24}$/i;
    if (typeof value === 'string') return regex.test(value);
    else if (typeof value === 'object') {
        if ('toString' in value && typeof value.toString === 'function') {
            try {
                return regex.test(value.toString());
            } catch (err) {
                return false;
            }
        } else return false;
    } else return false;
}

export const isObject = (value) => (typeof value === 'object' && !Array.isArray(value) && ![null, undefined].includes(value));

export const isValidString = (value) => {
    if (typeof value === 'string' && value.trim().length > 0) return true;

    return false;
}

export const ssnFormats = {
    'sweden': 'YYYYMMDDNNNN',
    'denmark': 'DDMMYYNNNN',
}

/**
 * 
 * @param {string} ssn 
 * @param {string | null} [defaultCountry] 
 * @returns 
 */
export const getSSNValue = (ssn, defaultCountry) => {
    try {
        ssn = ssn.trim().replace(/[^0-9]/g, '');
        if (!ssn) return null;

        let country = defaultCountry || 'sweden';
        if (country === 'sweden') {
            if (ssn.length === 12) return { country: 'sweden', ssn };
            if (ssn.length === 10) {
                let check = true;
                let currentYear = new Date().getFullYear();
                let dd = ssn.substr(0, 2);
                let mm = ssn.substr(2, 2);
                let yy = ssn.substr(4, 2);
                let nnnn = ssn.substr(6, 4);
                let yyyy = (Number(yy) + 2000) < currentYear ? `${Number(yy) + 2000}` : Number(yy) + 1900;

                dd = Number(dd);
                mm = Number(mm);
                if (dd <= 0 || dd > 31) {
                    check = false;
                }
                if (mm <= 0 || mm > 12) {
                    check = false;
                }
                if (yyyy < 1850 || yyyy > currentYear) {
                    check = false;
                }
                if (check) return { country: 'denmark', ssn };
            }
        } else if (country === 'denmark') {
            if (ssn.length === 10) return { country: 'denmark', ssn };
            if (ssn.length === 12) {
                let check = true;
                let currentYear = new Date().getFullYear();
                let yyyy = ssn.substr(0, 4);
                let mm = ssn.substr(4, 2);
                let dd = ssn.substr(6, 2);
                let nnnn = ssn.substr(8, 4);

                yyyy = Number(yyyy);
                dd = Number(dd);
                mm = Number(mm);
                if (dd <= 0 || dd > 31) {
                    check = false;
                }
                if (mm <= 0 || mm > 12) {
                    check = false;
                }
                if (yyyy < 1850 || yyyy > currentYear) {
                    check = false;
                }
                if (check) return { country: 'sweden', ssn };
            }
        }
    } catch (err) {
        console.log('getSSNValue', err);
    }

    return null;
}

/**
 * 
 * @param {{country?: ('denmark' | 'sweden'); ssn?: string;}} [value] 
 * @returns {boolean}
 */
export const isValidSSN = (value) => {
    try {
        if (isObject(value) && value.country && ['sweden', 'denmark'].includes(value.country) && typeof value.ssn === 'string') {
            if (value.ssn === '') return true;

            let { country, ssn } = value;
            ssn = ssn.trim().replace(/[^0-9]/g, '');

            if (!ssn) return false;

            if (country === 'denmark') {
                if (ssn.length !== 10) return false;
                const day = Number(`${ssn[0]}${ssn[1]}`);
                const month = Number(`${ssn[2]}${ssn[3]}`);
                const yearLastDigits = Number(`${ssn[4]}${ssn[5]}`);

                if (!day || !month) return false;
                if (day < 1 || day > 31) return false;
                if (month < 1 || month > 12) return false;

                const nowYear = new Date().getFullYear();
                const nowYearLastDigits = `${String(nowYear)[2]}${String(nowYear)[3]}`;

                let year;
                if (yearLastDigits === 0) year = 2000;
                else {
                    if (yearLastDigits > (+nowYearLastDigits)) year = Number(`19${yearLastDigits < 10 ? '0' + yearLastDigits : yearLastDigits}`);
                    else year = Number(`20${yearLastDigits < 10 ? '0' + yearLastDigits : yearLastDigits}`);
                }

                if (year > nowYear || year < (nowYear - 150)) return false;

                let daysInMoth = new Date(year, month, 0).getDate();

                if (day > daysInMoth) return false;

                return true;
            } else {
                if (ssn.length !== 12) return false;

                let year = parseInt(`${ssn[0]}${ssn[1]}${ssn[2]}${ssn[3]}`);
                let month = parseInt(`${ssn[4]}${ssn[5]}`);
                let day = parseInt(`${ssn[6]}${ssn[7]}`);
                //let date = new Date(year, month - 1, day);
                if (month < 1 || month > 12) {
                    return false;
                }

                if (day < 1 || day > 31) {
                    return false;
                }
                let yearNow = new Date().getFullYear()
                let limitYearUp = 2200;
                let limitYearDown = 1850;
                if (yearNow) {
                    limitYearUp = yearNow;
                    limitYearDown = yearNow - 150;
                }

                if (year < limitYearDown || year > limitYearUp) {
                    return false;
                }

                let daysInMoth = new Date(year, month, 0).getDate();
                if (day > daysInMoth) {
                    return false;
                }


                return true;
            }
        }
    } catch (err) {
        console.log('isValidSSN', err);
    }

    return false;
}

export const getDateValue = (something) => {
    try {
        const date = new Date(something);
        if (isValidNumber(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
    } catch (err) {
        console.log('getDateValue', err);
    }

    return '';
}

export const isValidDate = (value) => {
    try {
        if (typeof value === 'string') {
            const [year, month, day] = value.split('-').map(Number);
            if (isValidNumber(year) && year && isValidNumber(month) && month && isValidNumber(day) && day && month > 0 && month < 13 && day > 0 && day < 32) {
                const date = new Date(`${year}-${month}-${day}`);
                if (isValidNumber(date.getTime())) return true;
            }
        }
    } catch (err) {
        console.log('isValidDate', err);
    }

    return false;
}

/**
 * 
 * @param {('ssn' | 'dob')} type 
 * @param {string | {country?: string; ssn?: string;} | null} [value] 
 */
export const getAge = (type, value) => {
    try {
        let day;
        let month;
        let year;

        if (type === 'ssn') {
            if (isValidSSN(value) && value?.ssn) {
                let ssn = value.ssn.trim().replace(/[^0-9]/g, '');
                if (value?.country === 'denmark') {
                    day = Number(`${ssn[0]}${ssn[1]}`);
                    month = Number(`${ssn[2]}${ssn[3]}`);
                    const yearLastDigits = Number(`${ssn[4]}${ssn[5]}`);
                    const nowYear = new Date().getFullYear();
                    const nowYearLastDigits = `${String(nowYear)[2]}${String(nowYear)[3]}`;
                    if (yearLastDigits === 0) year = 2000;
                    else {
                        if (yearLastDigits > +nowYearLastDigits) year = Number(`19${yearLastDigits < 10 ? '0' + yearLastDigits : yearLastDigits}`);
                        else year = Number(`20${yearLastDigits < 10 ? '0' + yearLastDigits : yearLastDigits}`);
                    }
                } else {
                    year = parseInt(`${ssn[0]}${ssn[1]}${ssn[2]}${ssn[3]}`);
                    month = parseInt(`${ssn[4]}${ssn[5]}`);
                    day = parseInt(`${ssn[6]}${ssn[7]}`);
                }
            }
        } else if (type === 'dob') {
            if (isValidDate(value) && value?.trim?.()) {
                const [y, m, d] = value.split('-').map(Number);
                year = y;
                month = m;
                day = d;
            }
        }

        if (year && month && day) {
            const currentDate = new Date();
            const birthDate = new Date(year, month - 1, day);
            birthDate.setHours(0, 0, 0, 0);

            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth();
            const currentDay = currentDate.getDate();

            const birthYear = birthDate.getFullYear();
            const birthMonth = birthDate.getMonth();
            const birthDay = birthDate.getDate();

            let age = currentYear - birthYear;

            if (birthMonth > currentMonth || (birthMonth === currentMonth && birthDay > currentDay)) {
                age--;
            }

            return age;
        }
    } catch (err) {

    }

    return null;
}

export const camelCaseToCapitalizedString = (str) => str.replace(/([A-Z])/g, ' $1').trim().replace(/^./, str => str.toUpperCase());

export const getValueFromObject = (
    obj,
    key
) => {
    if (key.includes('.')) {
        return String(key).split('.').reduce((o, k) => (o || {})[k], obj);
    } else {
        return obj[key];
    }
}

export const stringWithDotToObject = (
    str,
    value
) => {
    const keys = str.split('.');
    if (keys.length === 2) return [{ [keys[1]]: value }, keys[0]];

    const firstKey = keys.shift();
    const lastKey = keys.pop();

    const obj = keys.reduceRight((acc, key) => ({ [key]: acc }), { [lastKey]: value });

    return [obj, firstKey]
}

/**
 * 
 * @param {import("./types").FormV2Field['cols']} cols 
 * @param {import("./types").FormV2State} state 
 * @param {Record<string, any>} externalState 
 * @returns {{sm: import("./types").Cols; md: import("./types").Cols; lg: import("./types").Cols; xl: import("./types").Cols; xxl: import("./types").Cols}}
 */
export function getCols(cols, state, externalState) {
    if (!cols) return { sm: 12, md: 12, lg: 12, xl: 12, xxl: 12 };
    let c = typeof cols === 'function' ? cols(state, externalState) : cols;
    return (typeof c === 'object' ? { sm: c.sm || 12, md: c.md || 12, lg: c.lg || 12, xl: c.xl || c.lg || 12, xxl: c.xxl || c.xl || c.lg || 12 } : { sm: c, md: c, lg: c, xl: c, xxl: c });
};

export const getOptions = (opts, state, externalState) => {
    let options;
    if (opts) {
        let arr = [];
        if (Array.isArray(opts)) arr = opts;
        else if (typeof opts === 'function') arr = opts(state, externalState);

        options = arr.map(opt => {
            if (typeof opt === 'string') {
                return {
                    label: camelCaseToCapitalizedString(opt),
                    value: opt,
                };
            } else if (typeof opt === 'object') {
                return {
                    label: opt.label || opt.value,
                    value: opt.value || opt.label,
                };
            }

            return null;
        }).filter(opt => opt !== null);
    }

    return options;
};

export const getIsRequired = (isRequired, state, externalState) => {
    let required = false;
    if (isRequired) {
        if (typeof isRequired === 'boolean') return isRequired;
        if (typeof isRequired === 'function') return isRequired(state, externalState);
    }

    return required;
};

export const getIsDisabled = (isDisabled, state, externalState) => {
    let disabled = false;
    if (isDisabled) {
        if (typeof isDisabled === 'boolean') return isDisabled;
        if (typeof isDisabled === 'function') return isDisabled(state, externalState);
    }

    return disabled;
};

export const getStyles = (style, state, externalState) => {
    if (style) {
        if (typeof style === 'function') return style(state, externalState);

        return style;
    }

    return {};
};

export const getClassNames = (classNames, state, externalState) => {
    if (classNames) {
        if (typeof classNames === 'function') return classNames(state, externalState);

        return classNames;
    }

    return {};
}

export const getHideErrorMessage = (hideErrorMessage, state, externalState) => {
    if (hideErrorMessage) {
        if (typeof hideErrorMessage === 'boolean') return hideErrorMessage;
        if (typeof hideErrorMessage === 'function') return hideErrorMessage(state, externalState);
    }

    return false;
};

export const getLabel = (label, state, externalState) => {
    if (label) {
        if (typeof label === 'function') return label(state, externalState);

        return label;
    }

    return undefined;
};

export const getDescription = (description, state, externalState) => {
    if (description) {
        if (typeof description === 'function') return description(state, externalState);

        return description;
    }

    return undefined;
};

export const getPlaceholder = (placeholder, state, externalState) => {
    if (placeholder) {
        if (typeof placeholder === 'function') return placeholder(state, externalState);

        return placeholder;
    }

    return undefined;
};

export const getAria = (errorMessage, description, fieldId, isMessageHidden, isTouched, isValid) => {
    return {
        'aria-describedby': (errorMessage || description) && fieldId && !isMessageHidden ? fieldId + '-message' : undefined,
        'aria-invalid': errorMessage ? 'true' : (isTouched && isValid) ? 'false' : undefined,
    }
}